import { Link } from "wouter";
import { useRef, useMemo, useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useShopStatus } from "@/hooks/useShopStatus";
import { getAllArtists, getExperienceLabel, fetchArtistsFromApi, type ArtistWithPortfolio } from "@/lib/artists";
import { ArrowRight, MapPin, Clock, Mail, Phone, Store, DollarSign, Palette, Activity, CalendarDays, Images } from "lucide-react";
import { useSiteConfig } from "@/lib/SiteConfigContext";
import { useStudioConfig } from "@/hooks/use-studio-config";
import { magikVars, magikVarsSmall } from "@/lib/magik-styles";
import { OptimizedImage } from "@/components/OptimizedImage";
import { buildImageSrcSet } from "@shared/image-srcset";
import { usePrefetchRoute } from "@/hooks/use-prefetch-route";
import { JsonLd } from "@/components/JsonLd";


interface LiveAvailability {
  artistSlug: string;
  status: string;
  nextAvailableDate: string | null;
  note: string | null;
}

export default function Home() {
  const { t } = useTranslation("home");
  const prefetchBooking = usePrefetchRoute(
    "booking-calendar",
    () => import("@/pages/BookingCalendar"),
    2000,
  );
  const prefetchGallery = usePrefetchRoute(
    "gallery",
    () => import("@/pages/Gallery"),
  );
  const prefetchAbout = usePrefetchRoute(
    "about",
    () => import("@/pages/About"),
  );
  const prefetchEstimate = usePrefetchRoute(
    "cost-estimator",
    () => import("@/pages/CostEstimator"),
  );
  const prefetchArtist = usePrefetchRoute(
    "artist",
    () => import("@/pages/Artist"),
    3000,
  );

  const { data: apiArtists } = useQuery<ArtistWithPortfolio[]>({
    queryKey: ["/api/artists"],
    queryFn: fetchArtistsFromApi,
    staleTime: 60 * 1000,
  });
  const artists = getAllArtists(apiArtists);
  const [failedArtistImages, setFailedArtistImages] = useState<Set<string>>(new Set());
  const shopStatus = useShopStatus();
  const { config: studio } = useStudioConfig();
  const { data: availabilityList = [] } = useQuery<LiveAvailability[]>({
    queryKey: ["/api/availability"],
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
  const availabilityMap = useMemo(() => {
    const map = new Map<string, LiveAvailability>();
    availabilityList.forEach((a) => map.set(a.artistSlug, a));
    return map;
  }, [availabilityList]);

  const { config } = useSiteConfig();
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentIndexRef = useRef(0);

  const playlist = useMemo(() => {
    const raw = studio.heroVideoPlaylist;
    if (raw && raw.length > 0) return raw;
    const fallbackUrl = studio.heroVideoUrl || "/storage/videos/hero-bg.mp4";
    return [{ url: fallbackUrl, loopStart: studio.heroVideoLoopStart ?? 2.0, loopEnd: studio.heroVideoLoopEnd ?? null }];
  }, [studio.heroVideoPlaylist, studio.heroVideoUrl, studio.heroVideoLoopStart, studio.heroVideoLoopEnd]);

  const advanceVideo = useCallback((v: HTMLVideoElement, nextIndex: number) => {
    const entry = playlist[nextIndex];
    if (!entry) return;
    currentIndexRef.current = nextIndex;
    v.src = entry.url;
    v.load();
    v.currentTime = entry.loopStart ?? 0;
    v.play().catch(() => { /* intentionally silent: best-effort side-effect */ });
  }, [playlist]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || playlist.length === 0) return;
    currentIndexRef.current = 0;
    const first = playlist[0];
    if (first.loopStart && first.loopStart > 0) {
      const onFirstLoad = () => { v.currentTime = first.loopStart ?? 0; };
      v.addEventListener("loadedmetadata", onFirstLoad, { once: true });
    }
    let rafId: number;
    const THRESHOLD = 0.15;
    const tick = () => {
      if (v.readyState >= 2 && !v.paused && !v.seeking && v.duration > 0) {
        const entry = playlist[currentIndexRef.current];
        const shouldAdvance = entry?.loopEnd != null
          ? v.currentTime >= entry.loopEnd
          : v.duration - v.currentTime <= THRESHOLD;
        if (shouldAdvance) {
          const nextIndex = (currentIndexRef.current + 1) % playlist.length;
          advanceVideo(v, nextIndex);
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [playlist, advanceVideo]);

  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>(".home-section-fadein");
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in-view"); observer.unobserve(e.target); } });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [studio.teamPhotoUrl]);


  const heroGlowStyle = useMemo(
    () => magikVars(config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed),
    [config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed]
  );
  const smallGlowStyle = useMemo(
    () => magikVarsSmall(config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed),
    [config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed]
  );

  const baseUrl = typeof window !== "undefined" ? window.location.origin : (studio.siteUrl || "");
  const jsonLdData = useMemo<Record<string, unknown>>(() => ({
    "@context": "https://schema.org",
    "@type": "TattooParlor",
    name: studio.name,
    description: studio.description || `Custom tattoo studio in ${studio.city}, ${studio.state}.`,
    url: baseUrl,
    telephone: studio.phone,
    image: studio.heroImage ? `${baseUrl}${studio.heroImage}` : undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: studio.address,
      addressLocality: studio.city,
      addressRegion: studio.state,
      postalCode: studio.zip,
      addressCountry: "US",
    },
    ...(studio.lat && studio.lng ? {
      geo: {
        "@type": "GeoCoordinates",
        latitude: studio.lat,
        longitude: studio.lng,
      },
    } : {}),
    openingHours: studio.hours || undefined,
    ...(studio.established ? { foundingDate: String(studio.established) } : {}),
    ...((studio as unknown as { sameAs?: string[] }).sameAs
      ? { sameAs: (studio as unknown as { sameAs?: string[] }).sameAs }
      : {
          sameAs: [studio.instagramUrl, studio.facebookUrl].filter((x): x is string => !!x),
        }),
  }), [studio, baseUrl]);

  return (
    <div className="space-y-24 pb-24 overflow-x-hidden">
      <JsonLd id="home-jsonld" data={jsonLdData} />

      <section className="relative min-h-screen flex items-center justify-center -mt-20 pt-20 px-4 md:px-6">
        <div
          className="absolute inset-0 z-0"
          style={{ backgroundColor: "hsl(0 0% 5%)" }}
        />

        {/* profile-guard-ok: hero card container is identical across client/demo profiles;
            max-w-6xl keeps the card from stretching too wide on large viewports */}
        <div
          className="relative z-10 w-full max-w-6xl mx-auto rounded-3xl overflow-hidden border border-white/10"
          style={{
            boxShadow: `0 0 120px rgba(139,92,246,0.15), 0 0 60px rgba(236,72,153,0.08), 0 0 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)`,
          }}
        >
          <div
            className="absolute inset-0 rounded-3xl"
            style={{
              background: `linear-gradient(135deg, rgb(15,12,20) 0%, rgb(10,8,16) 40%, rgb(20,14,30) 100%)`,
              opacity: config.heroCardBgOpacity / 100,
            }}
          />
          <div className="hero-video-bg hero-video-fade-wrapper absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              preload="none"
              onLoadedMetadata={() => {
                if (videoRef.current) videoRef.current.currentTime = 2;
              }}
              onEnded={() => {
                const v = videoRef.current;
                if (v) { v.currentTime = 2; v.play(); }
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: (config.heroVideoOpacity ?? 18) / 100,
                filter: "saturate(0.6) brightness(0.7)",
              }}
              data-testid="hero-video"
            >
              <source src={playlist[0]?.url || "/storage/videos/hero-bg.mp4"} type="video/mp4" />
            </video>
          </div>
          {(config.heroWispsEnabled ?? true) && <div className="hero-wisps absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
            <div
              className="hero-wisp absolute sm:w-[400px] sm:h-[400px] w-[200px] h-[200px] rounded-full blur-[80px]"
              style={{
                top: "-15%",
                right: "-8%",
                background: `radial-gradient(circle, ${config.magikGlowColor1} 0%, transparent 70%)`,
                opacity: 0.3,
                animation: `wispFloat1 ${config.magikGlowSpeed * 3}s ease-in-out infinite alternate`,
              }}
            />
            <div
              className="hero-wisp absolute sm:w-[450px] sm:h-[350px] w-[200px] h-[175px] rounded-full blur-[80px]"
              style={{
                bottom: "-15%",
                left: "-12%",
                background: `radial-gradient(ellipse, ${config.magikGlowColor2} 0%, transparent 70%)`,
                opacity: 0.25,
                animation: `wispFloat2 ${config.magikGlowSpeed * 3.5}s ease-in-out infinite alternate-reverse`,
              }}
            />
            <div
              className="hero-wisp absolute w-[350px] h-[400px] rounded-full blur-[70px]"
              style={{
                top: "15%",
                left: "25%",
                background: `radial-gradient(ellipse, ${config.magikGlowColor1} 0%, transparent 65%)`,
                opacity: 0.18,
                animation: `wispFloat3 ${config.magikGlowSpeed * 4}s ease-in-out infinite alternate`,
              }}
            />
          </div>}

          <div className="relative z-10 text-center px-6 md:px-12 py-24 md:py-32 lg:py-40">
            {/* profile-guard-ok: backdrop wrapper is conditional on heroTextBackdropEnabled —
                when disabled, no max-w-3xl constraint is added to this shared client/demo hero.
                layout-intent: max-w-3xl constrains only the semi-transparent backdrop panel,
                not the full-width hero container, so the backdrop visually frames the text
                without spanning the entire viewport width. */}
            <div
              className={config.heroTextBackdropEnabled ? "mx-auto max-w-3xl px-8 py-10" : ""}
              style={config.heroTextBackdropEnabled ? {
                background: (() => {
                  const h = config.heroTextBackdropColor ?? "#000000";
                  const r = parseInt(h.slice(1, 3), 16);
                  const g = parseInt(h.slice(3, 5), 16);
                  const b = parseInt(h.slice(5, 7), 16);
                  return `rgba(${r},${g},${b},${(config.heroTextBackdropOpacity ?? 45) / 100})`;
                })(),
                backdropFilter: (config.heroTextBackdropBlur ?? 0) > 0
                  ? `blur(${config.heroTextBackdropBlur}px)` : undefined,
                borderRadius: config.heroTextBackdropRadius ?? 24,
              } : undefined}
            >
            <div className="home-hero-fadein">
              <h2 className="text-white uppercase tracking-[0.3em] text-sm md:text-base font-bold mb-4 font-subheading">
                {studio.established ? `Est. ${studio.established}` : t("hero.welcome")}
              </h2>
              <h1
                className="text-5xl md:text-7xl lg:text-8xl font-heading mb-4 text-white tracking-wider leading-tight"
                style={{ textShadow: config.heroTextShadow > 0 ? `0 2px ${config.heroTextShadow}px rgba(0,0,0,0.9), 0 4px ${config.heroTextShadow * 2}px rgba(0,0,0,0.6)` : "none" }}
              >
                {studio.name ? (
                  <span
                    className="magik-glow-text italic"
                    style={heroGlowStyle}
                  >{studio.name}</span>
                ) : (
                  <span
                    className="inline-block align-middle rounded-lg bg-white/10 animate-pulse"
                    style={{ width: 420, maxWidth: "80vw", height: "1em" }}
                    aria-hidden="true"
                  />
                )}
              </h1>
              <span className="inline-block bg-accent/20 border border-accent/40 text-white text-xs md:text-sm uppercase tracking-[0.2em] font-subheading font-bold px-5 py-1.5 rounded-full mb-8">
                {studio.slogan || t("hero.slogan")}
              </span>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-12 font-light tracking-wide leading-relaxed font-subheading">
                {studio.description || "A local tattoo studio featuring talented artists in a clean and cozy environment."}
              </p>
              <div className="flex flex-col md:flex-row gap-6 justify-center">
                <a
                  href="#artists"
                  className="group bg-accent text-white px-8 py-4 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)]"
                  data-testid="link-meet-artists"
                >
                  {t("hero.cta_meet_artists")}{" "}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>
                <Link
                  href="/booking"
                  className="btn-cta-glow bg-accent text-white px-8 py-4 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 flex items-center justify-center gap-2"
                  data-testid="link-book-consultation"
                  onMouseEnter={prefetchBooking}
                  onFocus={prefetchBooking}
                >
                  <CalendarDays size={14} /> {t("hero.cta_book_appointment")}
                </Link>
                <a
                  href="tel:+19897284278"
                  className="bg-accent text-white px-8 py-4 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)]"
                  data-testid="link-call-shop"
                >
                  <Phone size={14} /> {t("hero.cta_call_shop")}
                </a>
              </div>
            </div>
            </div>{/* end backdrop wrapper */}
          </div>
        </div>
      </section>

      {/* Group Photo Section */}
      {studio.teamPhotoUrl && (
      <section className="w-full" style={{ marginTop: 0 }} data-testid="section-group-photo">
        <div className="relative overflow-hidden home-section-fadein">
          {(() => {
            const tadj = studio.teamPhotoAdjustments ?? {};
            const posX = tadj.positionX ?? 50;
            const posY = tadj.positionY ?? 50;
            const brightness = tadj.brightness ?? 100;
            const contrast = tadj.contrast ?? 100;
            const opacity = tadj.opacity ?? 100;
            const rotate = tadj.rotate ?? 0;
            const zoom = tadj.zoom ?? 100;
            const hasCustomPosition = posX !== 50 || posY !== 50;
            const hasFilter = brightness !== 100 || contrast !== 100;
            const hasTransform = rotate !== 0 || zoom !== 100;
            const hasOpacity = opacity !== 100;
            const imgStyle = (hasCustomPosition || hasFilter || hasTransform || hasOpacity) ? {
              ...(hasCustomPosition ? { objectPosition: `${posX}% ${posY}%` } : {}),
              ...(hasFilter ? { filter: `brightness(${brightness / 100}) contrast(${contrast / 100})` } : {}),
              ...(hasOpacity ? { opacity: opacity / 100 } : {}),
              ...(hasTransform ? { transform: `rotate(${rotate}deg) scale(${zoom / 100})` } : {}),
            } : undefined;
            return (
              <OptimizedImage
                src={studio.teamPhotoUrl!}
                alt={`${studio.shortName} artists and friends group photo`}
                sizes="100vw"
                width={1200}
                height={600}
                priority
                data-testid="img-group-photo"
                className="w-full h-[500px] md:h-[600px] object-cover object-center"
                style={imgStyle}
              />
            );
          })()}
          {/* Top fade — photo emerges from the page background */}
          <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[hsl(0,0%,5%)] to-transparent pointer-events-none" />
          {/* Bottom fade — text area */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <h2 className="text-3xl md:text-5xl font-heading text-white tracking-wide">
              <span
                className="magik-glow-text italic"
                style={smallGlowStyle}
              >{studio.shortName}</span>
              {studio.brandSuffix && (
                <>{" "}<span className="text-white italic">{studio.brandSuffix}</span></>
              )}
            </h2>
            {studio.heroTagline && (
              <p className="text-muted-foreground font-light mt-4 max-w-xl text-lg leading-relaxed">
                {studio.heroTagline}
              </p>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Info Section */}
      <section className="container mx-auto px-6" data-testid="section-studio-info">
        <div className="grid md:grid-cols-2 gap-12 p-12 bg-black/80 backdrop-blur-sm border border-white/10 rounded-2xl relative overflow-hidden">
          <div className="space-y-6">
            <h2 className="font-heading text-3xl tracking-wide text-white border-l-2 border-accent pl-6">
              {t("studio_info.section_title")}
            </h2>
            <p className="text-muted-foreground leading-relaxed font-light" data-testid="text-studio-description">
              {t("studio_info.description")}
            </p>
          </div>

          <div className="flex flex-col justify-center space-y-6 md:pl-12 md:border-l border-white/5">
            <div className="flex items-start gap-4 text-muted-foreground">
              <MapPin className="text-accent shrink-0 mt-1" />
              <div>
                <h3 className="text-white uppercase tracking-wider text-xs mb-1 font-bold font-subheading">
                  {t("studio_info.location_label")}
                </h3>
                <p className="font-light">
                  <a
                    href={studio.mapUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                    data-testid="link-map-address"
                  >
                    {studio.fullAddress}
                  </a>
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 text-muted-foreground">
              <Clock className="text-accent shrink-0 mt-1" />
              <div>
                <h3 className="text-white uppercase tracking-wider text-xs mb-1 font-bold font-subheading">
                  {t("studio_info.hours_label")}
                </h3>
                <p className="font-light">{studio.hours || ""}</p>
                {studio.hoursNote && <p className="text-xs text-white/50 mt-1">{studio.hoursNote}</p>}
                <p className={`text-xs mt-1.5 font-medium ${shopStatus.isOpen ? "text-green-400" : "text-red-400/80"}`} data-testid="text-home-shop-status">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${shopStatus.isOpen ? "bg-green-400 animate-pulse" : "bg-red-400/80"}`} />
                  {shopStatus.label}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 text-muted-foreground">
              <Phone className="text-accent shrink-0 mt-1" />
              <div>
                <h3 className="text-white uppercase tracking-wider text-xs mb-1 font-bold font-subheading">
                  {t("studio_info.phone_label")}
                </h3>
                <p className="font-light">
                  <a href={`tel:${studio.phone}`} className="hover:text-accent transition-colors" data-testid="link-phone">
                    {studio.phone}
                  </a>
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 text-muted-foreground">
              <Mail className="text-accent shrink-0 mt-1" />
              <div>
                <h3 className="text-white uppercase tracking-wider text-xs mb-1 font-bold font-subheading">
                  {t("studio_info.email_label")}
                </h3>
                <p className="font-light">
                  <a
                    href="/contact"
                    className="hover:text-accent transition-colors"
                    data-testid="link-email"
                  >
                    Send us a message
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Artists Section */}
      <section id="artists" className="container mx-auto px-6">
        <div className="rounded-2xl p-8 md:p-12 mb-8 bg-black/80 backdrop-blur-sm border border-white/10">
          <h2 className="text-4xl md:text-5xl font-heading text-white mb-2 tracking-wide">
            {t("artists.section_title")}
          </h2>
          <p className="text-muted-foreground font-light">
            {t("artists.section_subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
            {artists.map((artist, artistIndex) => (
              <div
                key={artist.slug}
                className="group block relative rounded-2xl p-6 md:p-8 bg-black/80 backdrop-blur-sm border border-white/10"
                data-testid={`card-artist-${artist.slug}`}
              >
                <div
                  className="relative flex items-center justify-center"
                  style={{ marginBottom: "1rem" }}
                >
                  <div
                    className={`relative w-full ${config.artistCardBorderWidth > 0 ? "magik-glow-border" : ""}`}
                    style={{
                      maxWidth: `${400 * config.artistCardScale / 100}px`,
                      margin: "0 auto",
                      borderRadius: `${config.artistCardRadius}%`,
                      backgroundColor: "#000",
                      overflow: "hidden",
                      border: config.artistCardBorderWidth > 0
                        ? `${config.artistCardBorderWidth}px solid ${config.magikGlowColor1}`
                        : "none",
                      transition: "border 0.2s",
                      ...smallGlowStyle,
                    }}
                  >
                    {failedArtistImages.has(artist.slug) ? (
                      <div
                        className="w-full aspect-square flex items-center justify-center select-none"
                        style={{ backgroundColor: "#111", borderRadius: `${config.artistCardRadius}%` }}
                        aria-label={artist.name}
                      >
                        <span className="text-5xl font-bold text-white/50">{artist.name[0]}</span>
                      </div>
                    ) : (
                      <img
                        src={artist.image}
                        alt={`${artist.name} — ${artist.role} at ${studio.shortName}`}
                        loading={artistIndex === 0 ? "eager" : "lazy"}
                        decoding="async"
                        fetchPriority={artistIndex === 0 ? "high" : "auto"}
                        draggable={false}
                        width={400}
                        height={400}
                        srcSet={buildImageSrcSet(artist.image)}
                        sizes="(max-width: 640px) calc(50vw - 24px), (max-width: 1024px) calc(33vw - 24px), 400px"
                        className="w-full object-cover block aspect-square select-none transition-all duration-700 group-hover:scale-105"
                        style={{
                          backgroundColor: "#000",
                          objectPosition: `${(config.artistCardPositions?.[artist.slug] ?? { x: 50, y: 50 }).x}% ${(config.artistCardPositions?.[artist.slug] ?? { x: 50, y: 50 }).y}%`,
                          transform: (config.artistProfileZoom?.[artist.slug] ?? 100) !== 100 ? `scale(${(config.artistProfileZoom?.[artist.slug] ?? 100) / 100})` : undefined,
                          userSelect: "none",
                        }}
                        onError={() => setFailedArtistImages(prev => new Set([...prev, artist.slug]))}
                        onDragStart={(e) => e.preventDefault()}
                      />
                    )}
                    {config.artistCardVignetteOpacity > 0 && (
                      <div
                        className="absolute inset-0 pointer-events-none aspect-square"
                        style={{
                          borderRadius: `${config.artistCardRadius}%`,
                          background: `radial-gradient(circle at 50% ${config.artistCardVignetteCenter}%, transparent 30%, rgba(0,0,0,${config.artistCardVignetteOpacity / 100}) 100%)`,
                        }}
                      />
                    )}
                  </div>
                  <Link
                    href={`/artist/${artist.slug}`}
                    className="absolute inset-0 z-10 cursor-pointer"
                    aria-label={`View ${artist.name}'s profile`}
                    data-testid={`link-artist-photo-${artist.slug}`}
                    onMouseEnter={prefetchArtist}
                    onFocus={prefetchArtist}
                    onTouchStart={prefetchArtist}
                  />
                </div>

                <div className="text-center">
                  <Link
                    href={`/artist/${artist.slug}`}
                    data-testid={`button-artist-${artist.slug}`}
                    className="magik-glow-button inline-block text-white px-8 py-4 rounded-full uppercase tracking-widest font-subheading hover:bg-accent/80 transition-all duration-300 bg-black/60"
                    style={smallGlowStyle}
                    onMouseEnter={prefetchArtist}
                    onFocus={prefetchArtist}
                    onTouchStart={prefetchArtist}
                  >
                    <span className="block text-base font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">{artist.name}</span>
                  </Link>
                  <div className="mt-3 flex flex-col items-center gap-2" data-testid={`badge-experience-${artist.slug}`}>
                    <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold border border-amber-500/30 bg-amber-500/10 text-amber-400">
                      <Clock size={10} />
                      {getExperienceLabel(artist.tattooingSince, artist.tattooingSinceMonth)}
                    </span>
                    {(() => {
                      const live = availabilityMap.get(artist.slug);
                      const availStatus = live?.status || artist.availability?.status || "available";
                      const nextDate = live?.nextAvailableDate;
                      const nextLabel = nextDate
                        ? new Date(nextDate + "T00:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" })
                        : artist.availability?.nextAvailable;
                      return (
                        <span
                          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold border ${
                            availStatus === "booked"
                              ? "border-red-500/30 bg-red-500/10 text-red-400"
                              : availStatus === "limited"
                                ? "border-yellow-500/30 bg-yellow-500/10 text-yellow-400"
                                : "border-green-500/30 bg-green-500/10 text-green-400"
                          }`}
                          data-testid={`badge-availability-${artist.slug}`}
                        >
                          <CalendarDays size={10} />
                          {availStatus === "booked" && nextLabel
                            ? t("artists.availability_booked_with_date", { date: nextLabel })
                            : availStatus === "booked"
                              ? t("artists.availability_booked")
                              : availStatus === "limited"
                                ? nextLabel ? t("artists.availability_limited_with_date", { date: nextLabel }) : t("artists.availability_limited")
                                : t("artists.availability_accepting")}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="mt-3 text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-subheading mb-1.5">{t("artists.styles_label")}</p>
                  <div className="flex flex-wrap justify-center gap-2" data-testid={`badge-specialties-${artist.slug}`}>
                    {artist.specialties.map((spec, i) => {
                      const colors = [
                        { border: "border-amber-500/40", bg: "bg-amber-500/15", text: "text-amber-400" },
                        { border: "border-rose-500/40", bg: "bg-rose-500/15", text: "text-rose-400" },
                        { border: "border-cyan-500/40", bg: "bg-cyan-500/15", text: "text-cyan-400" },
                        { border: "border-violet-500/40", bg: "bg-violet-500/15", text: "text-violet-400" },
                        { border: "border-emerald-500/40", bg: "bg-emerald-500/15", text: "text-emerald-400" },
                        { border: "border-pink-500/40", bg: "bg-pink-500/15", text: "text-pink-400" },
                      ];
                      const c = colors[i % colors.length];
                      return (
                        <span
                          key={spec}
                          className={`px-4 py-1.5 border ${c.border} ${c.bg} ${c.text} text-[10px] uppercase tracking-widest font-bold rounded-full`}
                        >
                          {spec}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
      </section>

      {/* Contact / Booking CTA */}
      <section id="contact" className="container mx-auto px-6 mb-12">
        <div className="p-12 md:p-24 text-center relative overflow-hidden rounded-2xl bg-black/80 backdrop-blur-sm border border-white/10">
          {/* profile-guard-ok: max-w-2xl centres the CTA text block; identical on all client/demo profiles */}
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-heading mb-6 tracking-wide text-white">
              {t("cta_booking.heading")}
            </h2>
            <p className="text-muted-foreground mb-8 text-lg font-light leading-relaxed">
              {t("cta_booking.body")}
            </p>
            <Link
              href="/booking"
              data-testid="button-book-appointment"
              className="btn-cta-glow inline-flex items-center justify-center gap-2 bg-accent text-white px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 w-full md:w-auto"
              onMouseEnter={prefetchBooking}
              onFocus={prefetchBooking}
            >
              <CalendarDays size={16} /> {t("cta_booking.button")}
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery CTA */}
      <section className="container mx-auto px-6 mb-12">
        <div className="p-12 md:p-24 text-center relative overflow-hidden rounded-2xl bg-black/80 backdrop-blur-sm border border-white/10">
          {/* profile-guard-ok: max-w-2xl centres the CTA text block; identical on all client/demo profiles */}
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-heading mb-6 tracking-wide text-cyan-400">
              {t("cta_gallery.heading")}
            </h2>
            <p className="text-muted-foreground mb-8 text-lg font-light leading-relaxed">
              {t("cta_gallery.body")}
            </p>
            <Link
              href="/gallery"
              data-testid="button-view-gallery"
              className="inline-flex items-center justify-center gap-2 bg-accent text-white px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] w-full md:w-auto"
              onMouseEnter={prefetchGallery}
              onFocus={prefetchGallery}
            >
              <Images size={16} /> {t("cta_gallery.button")}
            </Link>
          </div>
        </div>
      </section>

      {/* Additional CTA Cards */}
      <section className="container mx-auto px-6 mb-4">
        <div className="grid md:grid-cols-2 gap-8">
          <div
            className="p-12 md:p-24 text-center relative overflow-hidden rounded-2xl bg-black/80 backdrop-blur-sm border border-white/10"
            data-testid="card-cta-about"
          >
            {/* profile-guard-ok: max-w-2xl centres the CTA text block; identical on all client/demo profiles */}
            <div className="relative z-10 max-w-2xl mx-auto">
              <h3 className="text-4xl md:text-5xl font-heading mb-6 tracking-wide text-amber-400">{t("cta_about.heading")}</h3>
              <p className="text-muted-foreground mb-8 text-lg font-light leading-relaxed">
                {t("cta_about.body", { studioName: studio.name, established: studio.established ? t("cta_about.body_established_suffix", { year: studio.established }) : "" })}
              </p>
              <Link
                href="/about"
                data-testid="button-cta-about"
                className="inline-flex items-center justify-center gap-2 bg-accent text-white px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] w-full md:w-auto"
                onMouseEnter={prefetchAbout}
                onFocus={prefetchAbout}
              >
                <Store size={16} /> {t("cta_about.button")}
              </Link>
            </div>
          </div>

          <div
            className="p-12 md:p-24 text-center relative overflow-hidden rounded-2xl bg-black/80 backdrop-blur-sm border border-white/10"
            data-testid="card-cta-pricing"
          >
            {/* profile-guard-ok: max-w-2xl centres the CTA text block; identical on all client/demo profiles */}
            <div className="relative z-10 max-w-2xl mx-auto">
              <h3 className="text-4xl md:text-5xl font-heading mb-6 tracking-wide text-emerald-400">{t("cta_pricing.heading")}</h3>
              <p className="text-muted-foreground mb-8 text-lg font-light leading-relaxed">
                {t("cta_pricing.body")}
              </p>
              <Link
                href="/estimate"
                data-testid="button-cta-pricing"
                className="inline-flex items-center justify-center gap-2 bg-accent text-white px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] w-full md:w-auto"
                onMouseEnter={prefetchEstimate}
                onFocus={prefetchEstimate}
              >
                <DollarSign size={16} /> {t("cta_pricing.button")}
              </Link>
            </div>
          </div>

          <div
            className="p-12 md:p-24 text-center relative overflow-hidden rounded-2xl bg-black/80 backdrop-blur-sm border border-white/10"
            data-testid="card-cta-quiz"
          >
            {/* profile-guard-ok: max-w-2xl centres the CTA text block; identical on all client/demo profiles */}
            <div className="relative z-10 max-w-2xl mx-auto">
              <h3 className="text-4xl md:text-5xl font-heading mb-6 tracking-wide text-cyan-400">{t("cta_quiz.heading")}</h3>
              <p className="text-muted-foreground mb-8 text-lg font-light leading-relaxed">
                {t("cta_quiz.body")}
              </p>
              <Link
                href="/quiz"
                data-testid="button-cta-quiz"
                className="inline-flex items-center justify-center gap-2 bg-accent text-white px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] w-full md:w-auto"
              >
                <Palette size={16} /> {t("cta_quiz.button")}
              </Link>
            </div>
          </div>

          <div
            className="p-12 md:p-24 text-center relative overflow-hidden rounded-2xl bg-black/80 backdrop-blur-sm border border-white/10"
            data-testid="card-cta-healing"
          >
            {/* profile-guard-ok: max-w-2xl centres the CTA text block; identical on all client/demo profiles */}
            <div className="relative z-10 max-w-2xl mx-auto">
              <h3 className="text-4xl md:text-5xl font-heading mb-6 tracking-wide text-rose-400">{t("cta_healing.heading")}</h3>
              <p className="text-muted-foreground mb-8 text-lg font-light leading-relaxed">
                {t("cta_healing.body")}
              </p>
              <Link
                href="/healing"
                data-testid="button-cta-healing"
                className="inline-flex items-center justify-center gap-2 bg-accent text-white px-10 py-5 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-all duration-300 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] w-full md:w-auto"
              >
                <Activity size={16} /> {t("cta_healing.button")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {studio.logoUrl && (
        <section className="container mx-auto px-6 pt-4 pb-8">
          <div className="flex justify-center">
            <img
              src={studio.logoUrl}
              alt={`${studio.name} — custom tattoo studio in ${studio.city}, ${studio.state}`}
              width={1047}
              height={1210}
              loading="lazy"
              className={`w-full max-w-xl mx-auto object-contain ${studio.logoBlackBg !== false ? "bg-black" : ""}`}
              data-testid="img-logo-floral"
            />
          </div>
        </section>
      )}
    </div>
  );
}
